// pricing-calculator/utils/eta.ts
// ETA calculations with optional parallel workstreams and platform approvals.

import type {
  CalcBase,
  Module,
  Platform,
  Range,
} from '@dopomogai/types';
import { ZERO_RANGE, addRange, addRangeToRecord, daysToWeeks, maxRange } from './range';

const DEFAULT_STREAM = 'general';
const PLATFORM_STREAM = 'platform';

/**
 * Select the primary workstream for an item.
 */
function primaryStream(streams?: string[]): string {
  return streams && streams.length > 0 ? streams[0] : DEFAULT_STREAM;
}

/**
 * Compute ETA in days for a set of work items:
 * - Base and each feature contribute to a stream (parallelization by taking max total across streams).
 * - Platform base ETA is treated as its own stream.
 *
 * Fallback: if no streams present anywhere, sum everything sequentially.
 */
export function computeEtaDays(base: CalcBase, features: Module[], platform?: Platform): Range {
  const hasStreams =
    (base.workstreams && base.workstreams.length > 0) ||
    features.some((f) => f.workstreams && f.workstreams.length > 0);

  // If no parallelization data, do a simple sequential sum
  if (!hasStreams) {
    let eta: Range = ZERO_RANGE;
    eta = addRange(eta, base.etaDays);
    if (platform) {
      eta = addRange(eta, platform.etaDays);
    }
    features.forEach((f) => {
      eta = addRange(eta, f.etaDays);
    });
    return eta;
  }

  // Parallel stream accumulation
  const streams: Record<string, Range> = {};

  // Base goes to its primary stream
  addRangeToRecord(streams, primaryStream(base.workstreams), base.etaDays);

  // Platform baseline gets its own stream
  if (platform) {
    addRangeToRecord(streams, PLATFORM_STREAM, platform.etaDays);
  }

  // Features contribute to their primary streams
  features.forEach((f) => {
    addRangeToRecord(streams, primaryStream(f.workstreams), f.etaDays);
  });

  // Compute parallel portion = max over stream totals
  let parallelPortion: Range = ZERO_RANGE;
  Object.values(streams).forEach((r) => {
    parallelPortion = maxRange(parallelPortion, r);
  });

  return parallelPortion;
}

/**
 * Convenience helper: compute ETA in weeks.
 */
export function computeEtaWeeks(base: CalcBase, features: Module[], platform?: Platform): Range {
  return daysToWeeks(computeEtaDays(base, features, platform));
}

/**
 * Aggregates all ETA pieces for testing/inspection.
 */
export function debugEtaBreakdown(base: CalcBase, features: Module[], platform?: Platform) {
  const streams: Record<string, Range> = {};
  addRangeToRecord(streams, primaryStream(base.workstreams), base.etaDays);
  if (platform) addRangeToRecord(streams, PLATFORM_STREAM, platform.etaDays);
  features.forEach((f) => addRangeToRecord(streams, primaryStream(f.workstreams), f.etaDays));

  const parallelStreamsTotal = Object.values(streams).reduce<Range>((acc, r) => maxRange(acc, r), ZERO_RANGE);

  return {
    streams,
    parallelStreamsTotal,
    etaDays: parallelStreamsTotal,
    etaWeeks: daysToWeeks(parallelStreamsTotal),
  };
}
  